import { Box, Grid, Heading, Link, Stack, Text } from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  bffApi,
  ParticipationRequestStatus,
  useUpdateParticipationRequestMutation,
  type ParticipationRequestDto,
  type ParticipationRequestPatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
} from "../../../../common/editing/save-coordinator";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";
import { saveWithVersionConflictRetry } from "../../buyer/public/saveWithVersionConflictRetry";
import { PortalLayout } from "../components/PortalLayout";
import { BidIntroStep } from "../bid/steps/BidIntroStep";
import { ExclusionStep } from "../bid/steps/ExclusionStep";
import { SupplierInfoStep } from "../bid/steps/SupplierInfoStep";
import { RequestQualificationStep } from "./RequestQualificationStep";
import { RequestReviewStep } from "./RequestReviewStep";
import { RequestWizardNav } from "./RequestWizardNav";
import { requestStepNeighbors, useRequestStepLabel, type RequestStep } from "./requestWizardSteps";
import { useRequestStepNavigation } from "./useRequestStepNavigation";

interface Props {
  eventId: string;
  requestId: string;
  request: ParticipationRequestDto;
  event: PortalSourcingEventDetailDto;
}

export const EditParticipationRequestForm = ({ eventId, requestId, request, event }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const [updateRequest] = useUpdateParticipationRequestMutation();
  const { currentStep, navigateToStep } = useRequestStepNavigation(eventId, requestId);
  const stepLabel = useRequestStepLabel();
  const readOnly = request.status !== ParticipationRequestStatus.Draft;

  const config = useMemo(
    () => ({
      initialState: request,
      save: async (fields: Partial<ParticipationRequestPatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateRequest({
              requestId,
              participationRequestPatchDto: retryFields as ParticipationRequestPatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getParticipationRequest.initiate({ requestId }, { forceRefetch: true }),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [request, requestId, dispatch, updateRequest, editorErrorConfig],
  );

  useCreateEditor<ParticipationRequestDto, ParticipationRequestPatchDto>(requestId, config);

  if (!currentStep) return <PortalLayout>{t("Loading")}</PortalLayout>;

  const goTo = (step: RequestStep | undefined): (() => void) | undefined =>
    step
      ? async () => {
          await coordinatorRegistry.flush(requestId);
          if (coordinatorRegistry.get(requestId)?.getEntityStatus() === "error") return;
          navigateToStep(step);
        }
      : undefined;
  const labelFor = (step: RequestStep | undefined): string | undefined => (step ? stepLabel(step) : undefined);
  const navPropsFor = (step: RequestStep) => {
    const { previous, next } = requestStepNeighbors(step);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: labelFor(next) };
  };

  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case "intro":
        return <BidIntroStep event={event} {...navPropsFor("intro")} />;
      case "supplier":
        return (
          <SupplierInfoStep
            entityId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("supplier")}
          />
        );
      case "exclusion":
        return (
          <ExclusionStep
            entityId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("exclusion")}
          />
        );
      case "qualification":
        return (
          <RequestQualificationStep
            requestId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("qualification")}
          />
        );
      case "review":
        return (
          <RequestReviewStep
            eventId={eventId}
            requestId={requestId}
            request={request}
            event={event}
            previousStep={navPropsFor("review").previousStep}
          />
        );
    }
  };

  return (
    <PortalLayout>
      <Stack spacing="6">
        <Box>
          <Link as={RouterLink} to={urls.portal.view.go(eventId)} color="smPrimary" fontSize="sm" fontWeight="semibold">
            ← {t("Back to the procurement")}
          </Link>
          <Heading size="lg" mt="2">
            {t("Request to participate")}
          </Heading>
          <Text color="smMuted" fontSize="sm">
            {event.title}
          </Text>
        </Box>

        <Grid templateColumns={{ base: "1fr", md: "260px 1fr" }} gap={{ base: "4", md: "8" }} alignItems="start">
          <Box display={{ base: "none", md: "block" }} position="sticky" top="6">
            <RequestWizardNav currentStep={currentStep} navigateToStep={navigateToStep} />
          </Box>
          <Box minW="0">{renderStep()}</Box>
        </Grid>
      </Stack>
    </PortalLayout>
  );
};
